/**
 * DGC genesis issuance table for public transparency (/genesis).
 * Keep in sync with scripts/genesis/cpc_allocations.template.json
 * and scripts/genesis/cpc_parameters.template.yaml.
 * Pool addresses are loaded at runtime from /cpc-genesis-allocations.json
 * (written by cpc_write_explorer_env from the ceremony file).
 *
 * Linear 10y pools: after bootstrap-vesting / bootstrap-onchain step 4,
 * funds sit in vesting::linear::Wallet objects (claim over 10 years).
 * platform_ecosystem has no vesting (circulating ops pool).
 */

export const CPC_GENESIS_TOTAL_SUPPLY = 1_000_000_000;

export type CpcGenesisPoolRow = {
	id: string;
	/** Display name (zh) */
	name: string;
	amount: number;
	/** Share of total supply, 0–100 */
	pct: number;
	vesting: 'linear_10y' | 'none' | 'system' | 'self_stake';
	note: string;
	/** Hex or display address when known (optional) */
	address?: string;
};

const poolsRaw = [
	{
		id: 'community',
		name: '社区建设',
		amount: 172_000_000,
		vesting: 'linear_10y' as const,
		note: '线性 10 年；创世打入池地址后由 bootstrap 装入链上 Wallet',
	},
	{
		id: 'foundation',
		name: '基金会',
		amount: 80_000_000,
		vesting: 'linear_10y' as const,
		note: '线性 10 年链上锁仓',
	},
	{
		id: 'tech_support',
		name: '技术支持',
		amount: 60_000_000,
		vesting: 'linear_10y' as const,
		note: '线性 10 年链上锁仓',
	},
	{
		id: 'platform_ecosystem',
		name: '平台生态',
		amount: 62_000_000,
		vesting: 'none' as const,
		note: '无 vesting；运营 / 生态流通（多签管理）',
	},
	{
		id: 'user_rewards',
		name: '用户奖励',
		amount: 20_000_000,
		vesting: 'linear_10y' as const,
		note: '线性 10 年链上锁仓',
	},
] as const;

export const CPC_GENESIS_VALIDATOR_COUNT = 3;
export const CPC_GENESIS_VALIDATOR_STAKE_EACH = 2_000_000;
export const CPC_GENESIS_VALIDATOR_STAKE_TOTAL =
	CPC_GENESIS_VALIDATOR_COUNT * CPC_GENESIS_VALIDATOR_STAKE_EACH;

export const CPC_GENESIS_STAKE_SUBSIDY_FUND = 600_000_000;

/** Epoch subsidy parameters — kept for tooling; not shown on /genesis */
export const CPC_GENESIS_SUBSIDY = {
	perEpoch: 60_000,
	periodLength: 1,
	decreaseRateBp: 1,
	epochDurationHours: 24,
} as const;

export const CPC_STAKE_LOCK_EPOCHS = 180;
export const CPC_GENESIS_COMMISSION_RATE_BP = 200;

function pctOfTotal(amount: number): number {
	return Math.round((amount / CPC_GENESIS_TOTAL_SUPPLY) * 10000) / 100;
}

export const CPC_GENESIS_ALLOCATION_ROWS: CpcGenesisPoolRow[] = [
	...poolsRaw.map((p) => ({
		id: p.id,
		name: p.name,
		amount: p.amount,
		pct: pctOfTotal(p.amount),
		vesting: p.vesting,
		note: p.note,
	})),
	{
		id: 'validators',
		name: '创世验证者自质押',
		amount: CPC_GENESIS_VALIDATOR_STAKE_TOTAL,
		pct: pctOfTotal(CPC_GENESIS_VALIDATOR_STAKE_TOTAL),
		vesting: 'self_stake',
		note: `${CPC_GENESIS_VALIDATOR_COUNT} 名验证者 × ${CPC_GENESIS_VALIDATOR_STAKE_EACH.toLocaleString('zh-CN')} DGC；本金锁 ${CPC_STAKE_LOCK_EPOCHS} epoch`,
	},
	{
		id: 'stake_subsidy_fund',
		name: 'Stake Subsidy 基金',
		amount: CPC_GENESIS_STAKE_SUBSIDY_FUND,
		pct: pctOfTotal(CPC_GENESIS_STAKE_SUBSIDY_FUND),
		vesting: 'system',
		note: '系统补贴池（创世打入 0x0）',
		address: '0x0',
	},
];

export function mergeGenesisPoolAddresses(
	rows: CpcGenesisPoolRow[],
	addressById: Record<string, string>,
): CpcGenesisPoolRow[] {
	return rows.map((row) => {
		const addr = (addressById[row.id] || row.address || '').trim();
		return addr ? { ...row, address: addr } : { ...row };
	});
}

export function formatGenesisDgc(amount: number): string {
	return `${amount.toLocaleString('zh-CN')} DGC`;
}

export function vestingLabel(v: CpcGenesisPoolRow['vesting']): string {
	switch (v) {
		case 'linear_10y':
			return '线性 10 年';
		case 'none':
			return '无锁定（流通池）';
		case 'system':
			return '系统池';
		case 'self_stake':
			return `质押锁 ${CPC_STAKE_LOCK_EPOCHS} epoch`;
		default:
			return '—';
	}
}

export function vestingHint(v: CpcGenesisPoolRow['vesting']): string | null {
	switch (v) {
		case 'linear_10y':
			return '链上 vesting::linear::Wallet；按时间线性 claim。打开池地址页可查看可领额度并领取。未 bootstrap 前可能仍是池地址上的 Coin';
		case 'none':
			return '无链上 vesting；可按运营规则转出 / 使用';
		case 'system':
			return '按 epoch 补贴参数发放，非普通地址持币';
		case 'self_stake':
			return '已进入验证者质押池，受质押解锁 epoch 约束';
		default:
			return null;
	}
}
